// Fetch every catalog batch before presenting a final count or empty state.
// Native search does not retrieve products by their collection metadata.
async function fetchCatalogSearch(url, signal) {
  const requestUrl = new URL(url, window.location.origin);
  requestUrl.searchParams.set('section_id', 'search-popup-results');
  requestUrl.searchParams.delete('page');
  let result;
  const ids = new Set();
  const visited = new Set();
  while (true) {
    if (visited.has(requestUrl.toString())) throw new Error('Repeated search page');
    visited.add(requestUrl.toString());
    const response = await fetch(requestUrl, { signal });
    if (!response.ok) throw new Error('Search request failed');
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const batch = doc.querySelector('[data-search-popup-results]');
    if (!batch) throw new Error('Search results missing');
    if (Number(batch.dataset.catalogSize) > 25000) throw new Error('Catalog exceeds theme search pagination limit');
    if (!result) {
      result = batch.cloneNode(true);
      result.querySelector('[data-search-products]').replaceChildren();
    }
    for (const card of batch.querySelectorAll('[data-product-id]')) {
      if (ids.has(card.dataset.productId)) continue;
      ids.add(card.dataset.productId);
      result.querySelector('[data-search-products]').append(card);
    }
    if (!batch.dataset.nextUrl) break;
    const next = new URL(batch.dataset.nextUrl, window.location.origin);
    requestUrl.searchParams.set('page', next.searchParams.get('page'));
  }
  result.removeAttribute('data-next-url');
  result.querySelector('[data-search-count]').textContent = `(${ids.size})`;
  result.querySelector('[data-search-empty]').hidden = ids.size !== 0;
  return result.outerHTML;
}

class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.abortController = null;
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('input', () => {
      this.abortController?.abort();
      this.removeAttribute('results');
      this.toggleAttribute('data-has-query', this.getQuery().length > 0);
    });
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('click', this.onSuggestionClick.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';

    const isTemplateSearch = this.closest('.template-search');
    if (isTemplateSearch) {
      document.addEventListener('click', this.handleClickOutside.bind(this));
    }
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  onFocus(event) {
    if (this.closest('.template-search')) {
      document.body.classList.add('predictive-search-open');
      if (this.getQuery().length === 0) {
        return;
      }
      const url = this.setupURL().toString();
      this.renderSection(url, event);
    }
  }
  getQuery() {
    return this.input.value.trim();
  }
  onKeydown(event) {
    if (event.key !== 'Enter' || !this.closest('search-drawer')) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const query = this.getQuery();
    if (!query) {
      return;
    }

    const searchUrl = new URL(this.action, window.location.origin);
    searchUrl.searchParams.set('q', query);
    searchUrl.searchParams.set('options[prefix]', 'last');
    if (this.dataset.searchType) {
      searchUrl.searchParams.set('type', this.dataset.searchType);
    }
    window.location.assign(searchUrl.toString());
  }
  onSuggestionClick(event) {
    const suggestion = event.target.closest('.predictive-search-result--queries a');
    if (!suggestion || !this.contains(suggestion)) {
      return;
    }

    event.preventDefault();
    const suggestionUrl = new URL(suggestion.href, window.location.origin);
    const query = suggestionUrl.searchParams.get('q') || suggestion.textContent.trim();
    if (!query) {
      return;
    }

    this.input.value = query;
    this.input.focus();
    this.onChange();
  }
  clear(event = null) {
    event && event.preventDefault();
    this.abortController?.abort();
    this.abortController = null;
    this.removeAttribute('loading');
    this.removeAttribute('error');
    this.removeAttribute('data-has-query');
    this.resetButton.classList.remove('btn--loading');
    if (this.searchRecommendationEmpty && this.searchContent) {
      this.searchContent.classList.add('hidden');
    }
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
  }

  setupURL() {
    if (this.dataset.searchType) {
      const url = new URL(this.action, window.location.origin);
      url.searchParams.set('q', this.getQuery());
      url.searchParams.set('type', this.dataset.searchType);
      url.searchParams.set('options[prefix]', 'last');
      url.searchParams.set('section_id', 'search-popup-results');
      return url;
    }

    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    return (
      url.searchParams.set('q', this.getQuery()),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.setAttribute('data-has-query', '');
    this.renderSection(url);
  }

  renderSection(url) {
    if (this.cachedMap.has(url)) {
      this.abortController?.abort();
      this.abortController = null;
      this.renderSectionFromCache(url);
    } else {
      this.renderSectionFromFetch(url);
    }
  }

  renderSectionFromCache(url) {
    this.removeAttribute('loading');
    this.removeAttribute('error');
    this.resetButton.classList.remove('btn--loading');
    const cachedResponse = this.cachedMap.get(url);
    this.renderSearchResults(cachedResponse.responseText);
    this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.abortController?.abort();
    const abortController = new AbortController();
    this.abortController = abortController;

    // Indicate that loading has started
    this.setAttribute('loading', 'true');
    this.resetButton.classList.add('btn--loading');

    this.removeAttribute('error');
    const resultsRequest = this.dataset.searchType
      ? fetchCatalogSearch(url, abortController.signal).then((responseText) => ({ responseText }))
      : fetch(url, { signal: abortController.signal }).then(async (response) => {
          if (!response.ok) throw new Error('Search request failed');
          return { responseText: await response.text() };
        });

    resultsRequest
      .then(({ responseText }) => {
        if (abortController.signal.aborted || url !== this.setupURL().toString()) return;
        // Process and cache the fetched data
        this.renderSearchResults(responseText);
        this.setAttribute('results', 'true');
        this.cachedMap.set(url, { responseText });
        if (this.cachedMap.size > 10) this.cachedMap.delete(this.cachedMap.keys().next().value);
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          return;
        }
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
        const message = document.createElement('p');
        message.setAttribute('role', 'alert');
        message.textContent = this.dataset.errorMessage;
        this.renderSearchResults(`<div data-search-popup-results>${message.outerHTML}</div>`);
        this.setAttribute('results', 'true');
      })
      .finally(() => {
        if (this.abortController !== abortController) {
          return;
        }
        this.abortController = null;
        // Remove the loading attribute in both success and error cases
        this.removeAttribute('loading');
        this.resetButton.classList.remove('btn--loading');
      });
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.querySelector('[data-search-popup-results]') || parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      setTimeout(() => {
        document.body.classList.remove('predictive-search-open');
      });
    }
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class SearchDrawer extends DrawerComponent {
  constructor() {
    super();
  }
  get requiresBodyAppended() {
    return false;
  }
  get input() {
    return this.querySelector('input[type="search"]');
  }
  get focusElement() {
    return this.querySelector('input[type="search"]');
  }
}
customElements.define('search-drawer', SearchDrawer);

class CatalogSearch extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    this.load();
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  async load() {
    const url = new URL(this.dataset.url, window.location.origin);
    url.searchParams.set('q', this.dataset.query);
    url.searchParams.set('type', 'product');
    try {
      const markup = await fetchCatalogSearch(url, this.controller.signal);
      if (this.controller.signal.aborted) return;
      this.querySelector('[data-catalog-results]').innerHTML = markup;
      this.list = this.querySelector('[data-search-products]');
      this.list.className = `products-list f-grid f-grid--gap-medium f-grid-${this.dataset.mobileColumns}-cols md:f-grid-3-cols xl:f-grid-${this.dataset.columns}-cols`;
      this.cards = Array.from(this.list.children);
      this.cards.forEach((card) => { card.className = 'f-column card'; });
      this.limit = Number(this.dataset.pageSize) || 16;
      this.querySelector('[data-search-more]').addEventListener('click', () => {
        this.limit += Number(this.dataset.pageSize) || 16;
        this.update();
      });
      this.querySelector('[data-search-sort]')?.addEventListener('change', () => this.update());
      const filters = this.querySelector('[data-search-filters]');
      if (filters) {
        filters.hidden = this.cards.length === 0;
        for (const key of ['type', 'vendor']) {
          const select = filters.querySelector(`[data-filter-${key}]`);
          const values = [...new Set(this.cards.map((card) => card.dataset[key]).filter(Boolean))].sort();
          values.forEach((value) => {
            const option = document.createElement('option');
            option.value = value;
            option.textContent = value;
            select.append(option);
          });
        }
        filters.addEventListener('input', () => {
          this.limit = Number(this.dataset.pageSize) || 16;
          this.update();
        });
      }
      const sortLabel = this.querySelector('[data-search-sort-label]');
      if (sortLabel) sortLabel.hidden = this.cards.length === 0;
      this.update();
      this.querySelector('[data-search-status]').hidden = true;
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.querySelector('[data-search-status]').hidden = true;
      this.querySelector('[data-search-error]').hidden = false;
    }
  }

  update() {
    const sort = this.querySelector('[data-search-sort]')?.value;
    const type = this.querySelector('[data-filter-type]')?.value;
    const vendor = this.querySelector('[data-filter-vendor]')?.value;
    const price = this.querySelector('[data-filter-price]')?.value;
    const cards = this.cards.filter((card) => (!type || card.dataset.type === type)
      && (!vendor || card.dataset.vendor === vendor)
      && (!price || Number(card.dataset.price) <= Number(price) * 100));
    if (sort?.startsWith('price')) cards.sort((a, b) => Number(a.dataset.price) - Number(b.dataset.price));
    if (sort?.startsWith('title')) cards.sort((a, b) => a.dataset.title.localeCompare(b.dataset.title));
    if (sort?.endsWith('descending')) cards.reverse();
    this.list.replaceChildren(...cards.slice(0, this.limit));
    this.querySelector('[data-search-count]').textContent = `(${cards.length})`;
    this.querySelector('[data-search-empty]').hidden = cards.length !== 0;
    this.querySelector('[data-search-more]').hidden = cards.length <= this.limit;
  }
}
customElements.define('catalog-search', CatalogSearch);
